import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { AlertCircle, ArrowRight, Check, ShieldCheck } from "lucide-react";

import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

/**
 * The page an invitation link opens.
 *
 * No login, no sidebar. It shows who invited them and as what, asks for a
 * password, and drops them straight into the app in the right role. Google
 * users can skip the password and use the sign-in button with the invited
 * address instead — the backend matches on email.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AcceptInvitePage() {
  const { token } = useParams();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/invite/${token}`)
      .then((res) => { setInvite(res.data); setName(res.data.name || ""); })
      .catch((err) => setError(err?.response?.data?.detail || "This invitation link is not valid."))
      .finally(() => setLoading(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("The two passwords don't match.");
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/public/invite/${token}/accept`, { password, name });
      login(res.data.user, res.data.token);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background p-4">
        <div className="max-w-md mx-auto pt-16 space-y-3">
          <div className="h-24 bg-muted rounded-xl animate-pulse" />
          <div className="h-56 bg-muted rounded-xl animate-pulse" />
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="text-center space-y-3 max-w-sm" data-testid="invite-invalid">
          <AlertCircle className="h-10 w-10 mx-auto text-muted-foreground" />
          <p className="text-foreground font-medium">{error}</p>
          <Button variant="outline" className="h-11" onClick={() => navigate("/login")}>
            Go to sign in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-md mx-auto pt-12 pb-16 space-y-5" data-testid="invite-page">
        <div className="text-center space-y-2">
          <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
            <ShieldCheck className="h-7 w-7 text-primary" aria-hidden="true" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">You're invited</h1>
          <p className="text-sm text-muted-foreground">
            {invite.invited_by_name ? `${invite.invited_by_name} has invited you` : "You've been invited"} to
            IVEM Food Portal as <strong className="text-foreground">{invite.role_label}</strong>.
          </p>
        </div>

        <Card>
          <CardContent className="p-5">
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="inv-email">Email</Label>
                <Input id="inv-email" className="h-11" value={invite.email} disabled
                       data-testid="invite-email" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inv-name">Your name</Label>
                <Input id="inv-name" className="h-11" value={name}
                       onChange={(e) => setName(e.target.value)} data-testid="invite-name" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inv-pass">Choose a password</Label>
                <Input id="inv-pass" type="password" className="h-11" value={password}
                       autoComplete="new-password"
                       onChange={(e) => setPassword(e.target.value)} data-testid="invite-password" />
                <p className="text-xs text-muted-foreground">At least 8 characters.</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="inv-confirm">Confirm password</Label>
                <Input id="inv-confirm" type="password" className="h-11" value={confirm}
                       autoComplete="new-password"
                       onChange={(e) => setConfirm(e.target.value)} data-testid="invite-confirm" />
              </div>

              {error && (
                <p className="text-sm text-destructive flex items-start gap-1.5" data-testid="invite-error">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden="true" />{error}
                </p>
              )}

              <Button type="submit" className="w-full h-11 gap-1.5" disabled={submitting}
                      data-testid="invite-submit">
                {submitting ? "Creating your account…" : <><Check className="h-4 w-4" aria-hidden="true" />Accept and sign in</>}
              </Button>
            </form>

            <div className="relative my-5">
              <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-border" /></div>
              <div className="relative flex justify-center text-xs"><span className="bg-card px-2 text-muted-foreground">or</span></div>
            </div>
            <Button variant="outline" className="w-full h-11 gap-1.5"
                    onClick={() => { window.location.href = `${API}/auth/google/login`; }}
                    data-testid="invite-google">
              Continue with Google using {invite.email}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
